import { useCallback, useState } from "react";
import { validation } from "vccm-common";
import { DialogProps } from "@mui/material/Dialog";
import { ProgressIndicator } from "../ProgressIndicator/ProgressIndicator";
import { mySecondaryColor } from "../../constants/colors";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  List,
  ListItem,
  TextField,
} from "@mui/material";
import PermIdentityIcon from "@mui/icons-material/PermIdentity";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

import { green, red } from "@mui/material/colors";

import {
  containsLowercase,
  containsNumber,
  containsUppercase,
  isMinLength,
} from "../../utilities/validations";
import { DialogWithBackdropClick } from "../DialogWithBackdropClick/DialogWithBackdropClick";
import { useVrsTranslationState } from "../../context/AppContext/AppContext";
import { styled } from "@mui/material/styles";

const StyledDialogWithBackdropClick = styled(DialogWithBackdropClick)(({ theme }) => ({
  '& .MuiDialog-paper': {
    padding: theme.spacing(0.125),
  }
}));

const FormControlBox = styled(FormControl)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  maxWidth: theme.spacing(70),
  flexFlow: 'row',
  alignItems: 'center',
  '&:nth-last-of-type(n+2)': {
    marginBottom: theme.spacing(1.875)
  }
}));

const TitleContainer = styled("div")({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center'
});

const StyledPermIdentityIcon = styled(PermIdentityIcon)(({ theme }) => ({
  fontSize: theme.spacing(6),
  color: mySecondaryColor,
  fontWeight: 600,
  marginRight: theme.spacing(1),
}));

const SyledDialogActions = styled(DialogActions)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(1, 2),
  }
}));

const CloseButtonContainer = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  right: theme.spacing(1),
  top: theme.spacing(1),
  color: theme.palette.grey[500],
}));

const PasswordCheckContainer = styled("div")({
  display: 'flex',
  alignItems: 'center'
});

const HeaderContainer = styled("div")(({ theme }) => ({
  fontSize: theme.spacing(2),
  fontWeight: 400
}));


const getIcon = (passed) => (
  <>
    {passed ? (
      <CheckIcon sx={{ color: green }} className="valid" data-testid="CheckIcon"/>
    ) : (
      <CloseIcon sx={{ color: red }} className="invalid" data-testid="CloseIcon"/>
    )}
  </>
);

interface NewPasswordDialogProps {
  loading: boolean;
  open: boolean;
  onClose: (value: string) => void;
  maxWidth?: DialogProps["maxWidth"];
  header?: string;
  disabled?: boolean;
}

const NewPasswordDialog = ({
  loading,
  open,
  maxWidth,
  onClose,
  header,
}: NewPasswordDialogProps) => {

  const { _T } = useVrsTranslationState();

  const [errors, setErrors] = useState<any>({});
  const [confirmPassword, setConfirmPassword] = useState("");
  const [password, setPassword] = useState("");

  const validateForm = useCallback(() => {
    // use the user schema to validate password rules
    const results = validation.schemas.user.validate(
      {
        password,
        confirmPassword,
      },
      { abortEarly: false }
    );
    const errs = results.error.details.reduce((agg, d) => {
      agg[d.context.key] = d.message ? d.message.replace(/\./g, "_") : "";
      return agg;
    }, {});
    setErrors(errs);

    return !(errs.confirmPassword || errs.password);
  }, [password, confirmPassword]);

  const handleCancel = () => {
    onClose("");
  };

  const handleOk = (password) => {
    onClose(password);
  };

  const checks = {
    passwordMatches:
      password && confirmPassword && password === confirmPassword,
    isMinLength: isMinLength(password, 8),
    containsNumber: containsNumber(password),
    containsUppercase: containsUppercase(password),
    containsLowercase: containsLowercase(password),
  };

  const allChecksPass = Object.keys(checks).reduce(
    (acc, el) => acc && checks[el],
    true
  );

  return (
    <>
      <StyledDialogWithBackdropClick
        disableBackdropClick
        fullWidth={true}
        maxWidth={maxWidth || "sm"}
        disableEscapeKeyDown
        aria-labelledby="new-password"
        open={open}
      >
        <DialogTitle id="password-dialog-title">
          <TitleContainer>
            <div>
              <StyledPermIdentityIcon data-testid="PermIdentityIcon"/>
            </div>
            <div>{_T("Password")}</div>
            {header && <HeaderContainer>&nbsp;({header})</HeaderContainer>}
          </TitleContainer>

          <CloseButtonContainer
            aria-label="close"
            data-testid="password-close-btn"
            onClick={handleCancel}
          >
            <CloseIcon data-testid="CloseIcon"/>
          </CloseButtonContainer>
        </DialogTitle>
        <DialogContent dividers>
          <FormControlBox>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label={_T("Password")}
              onChange={(event) => setPassword(event.target.value)}
              type="password"
              id="password"
              disabled={loading}
              value={password}
              error={errors && !!errors.password}
              helperText={errors && errors.password && _T(errors.password)}
            />
          </FormControlBox>
          <FormControlBox>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label={_T("Confirm_Password")}
              onChange={(event) => setConfirmPassword(event.target.value)}
              type="password"
              id="confirmPassword"
              disabled={loading}
              value={confirmPassword}
              error={errors && !!errors.confirmPassword}
              helperText={
                errors && errors.confirmPassword && _T(errors.confirmPassword)
              }
            />
          </FormControlBox>
          <div>
            <div>{_T("PasswordRequirements")}</div>
            <List>
              <ListItem>
                <PasswordCheckContainer>
                  {getIcon(checks.passwordMatches)}
                  {_T("PasswordsMustMatch")}
                </PasswordCheckContainer>
              </ListItem>
              <ListItem>
                <PasswordCheckContainer>
                  {getIcon(checks.isMinLength)}
                  {_T("PasswordIsMinLength")}
                </PasswordCheckContainer>
              </ListItem>
              <ListItem>
                <PasswordCheckContainer>
                  {getIcon(checks.containsNumber)}
                  {_T("PasswordContainsNumber")}
                </PasswordCheckContainer>
              </ListItem>
              <ListItem>
                <PasswordCheckContainer>
                  {getIcon(checks.containsUppercase)}
                  {_T("PasswordContainsUppercase")}
                </PasswordCheckContainer>
              </ListItem>
              <ListItem>
                <PasswordCheckContainer>
                  {getIcon(containsLowercase(password))}
                  {_T("PasswordContainsLowercase")}
                </PasswordCheckContainer>
              </ListItem>
            </List>
          </div>
        </DialogContent>
        <SyledDialogActions>
          <Button
            autoFocus
            onClick={() => {
              if (validateForm()) {
                handleOk(password);
              }
            }}
            data-testid="password-ok-btn"
            variant="contained"
            disabled={loading || !allChecksPass}
            color="primary"
          >
            {_T("Ok")}
          </Button>
        </SyledDialogActions>
      </StyledDialogWithBackdropClick>
      {loading && <ProgressIndicator />}
    </>
  );
};

export { NewPasswordDialog };
